"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type KeyFormat = "author-year" | "author-year-firstword" | "authoretal-year";
type Casing = "lowercase" | "camelcase";

type Strings = { [K in keyof typeof BIBTEX_KEY_GENERATOR_STRINGS]: string };

const KEY_FORMATS: {
  value: KeyFormat;
  label: keyof Strings;
  example: string;
}[] = [
  {
    value: "author-year",
    label: "formatAuthorYear",
    example: "smith2024",
  },
  {
    value: "author-year-firstword",
    label: "formatAuthorYearFirstWord",
    example: "smith2024quantum",
  },
  {
    value: "authoretal-year",
    label: "formatAuthorEtAlYear",
    example: "smithetal2024",
  },
];

const CASINGS: { value: Casing; label: keyof Strings }[] = [
  { value: "lowercase", label: "casingLowercase" },
  { value: "camelcase", label: "casingCamelcase" },
];

// --- Module-level regexes & maps (Biome: regexes must not live inside loops) ---

const RE_COMBINING_MARKS = /[\u0300-\u036f]/g;
const RE_NON_ALNUM = /[^a-zA-Z0-9]+/g;
const RE_AUTHOR_SPLIT = /\s*(?:;|,| and | & )\s*/i;
const RE_WHITESPACE = /\s+/;
const RE_PAREN_YEAR = /\(((?:18|19|20)\d{2})[a-z]?\)/;
const RE_YEAR_LOOSE = /\b((?:18|19|20)\d{2})\b/;
const RE_LEADING_NON_LETTER = /^[^A-Za-zÀ-ž]+/;
const RE_MULTI_AUTHOR_HINT = /,\s*[A-Z]|\band\b|&|;/;
const RE_NEWLINE = /\n/;

/** Letters with no Unicode decomposition that NFD cannot strip. */
const SPECIAL_LETTER_MAP: Record<string, string> = {
  ß: "ss",
  ø: "o",
  Ø: "O",
  đ: "d",
  Đ: "D",
  ð: "d",
  Ð: "D",
  þ: "th",
  Þ: "Th",
  ł: "l",
  Ł: "L",
  æ: "ae",
  Æ: "AE",
  œ: "oe",
  Œ: "OE",
};

const RE_SPECIAL_LETTERS = /[ßøØđĐðÐþÞłŁæÆœŒ]/g;

/** Words skipped when picking the "first significant word" of a title. */
const TITLE_STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "on",
  "of",
  "in",
  "at",
  "to",
  "for",
  "and",
  "or",
  "with",
  "from",
  "into",
  "about",
  "toward",
  "towards",
  "is",
  "are",
  "was",
  "were",
  "do",
  "does",
  "how",
  "why",
  "what",
  "when",
  "new",
]);

/** Strip accents and punctuation down to plain ASCII letters/digits. */
function cleanToken(raw: string): string {
  return raw
    .replace(RE_SPECIAL_LETTERS, (ch) => SPECIAL_LETTER_MAP[ch] ?? ch)
    .normalize("NFD")
    .replace(RE_COMBINING_MARKS, "")
    .replace(RE_NON_ALNUM, "");
}

/** First significant (3+ letter, non-stop-word) word of a title, cleaned. */
function firstTitleWord(title: string): string {
  for (const raw of title.trim().split(RE_WHITESPACE)) {
    const cleaned = cleanToken(raw);
    if (cleaned.length >= 3 && !TITLE_STOP_WORDS.has(cleaned.toLowerCase())) {
      return cleaned;
    }
  }
  return "";
}

function capitalize(part: string): string {
  if (part.length === 0) {
    return part;
  }
  return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
}

interface KeyParts {
  surnames: string[];
  year: string;
  title: string;
}

/** Assemble the key parts (before casing) for the chosen format. */
function keyPieces(parts: KeyParts, format: KeyFormat): string[] {
  const author = cleanToken(parts.surnames[0] ?? "");
  const year = cleanToken(parts.year);
  const pieces = [author];
  if (format === "authoretal-year" && parts.surnames.length > 1) {
    pieces.push("EtAl");
  }
  pieces.push(year);
  if (format === "author-year-firstword") {
    pieces.push(firstTitleWord(parts.title));
  }
  return pieces.filter((p) => p.length > 0);
}

/** Build a single citation key from its parts. */
function buildKey(parts: KeyParts, format: KeyFormat, casing: Casing): string {
  const pieces = keyPieces(parts, format);
  if (pieces.length === 0) {
    return "";
  }
  if (casing === "lowercase") {
    return pieces.join("").toLowerCase();
  }
  return pieces.map(camelPiece).join("");
}

/** CamelCase a key piece, keeping years and the "EtAl" marker as-is. */
function camelPiece(piece: string): string {
  if (RE_YEAR_LOOSE.test(piece) || piece === "EtAl") {
    return piece;
  }
  return capitalize(piece);
}

/** Best-effort parse of one formatted reference line into key parts. */
function parseReference(line: string): KeyParts {
  const parenYear = RE_PAREN_YEAR.exec(line);
  const looseYear = parenYear ?? RE_YEAR_LOOSE.exec(line);
  const year = looseYear ? looseYear[1] : "";

  // Author segment = text before the year (or before the first period).
  const yearIdx = looseYear ? line.indexOf(looseYear[0]) : -1;
  const periodIdx = line.indexOf(". ");
  let authorSeg = line;
  if (yearIdx > 0) {
    authorSeg = line.slice(0, yearIdx);
  } else if (periodIdx > 0) {
    authorSeg = line.slice(0, periodIdx);
  }
  const surname = authorSeg.trim().split(RE_WHITESPACE)[0] ?? "";
  const multiAuthor = RE_MULTI_AUTHOR_HINT.test(
    authorSeg.slice(surname.length)
  );

  // Title segment = text after the year (or after the first period).
  let titleSeg = "";
  if (looseYear && yearIdx >= 0) {
    titleSeg = line.slice(yearIdx + looseYear[0].length);
  } else if (periodIdx > 0) {
    titleSeg = line.slice(periodIdx + 1);
  }
  titleSeg = titleSeg.replace(RE_LEADING_NON_LETTER, "");

  return {
    surnames: multiAuthor ? [surname, "etal-hint"] : [surname],
    year,
    title: titleSeg,
  };
}

/** Append a/b/c… suffixes to keys that collide (all colliding keys get one). */
function disambiguate(keys: string[]): string[] {
  const counts = new Map<string, number>();
  for (const key of keys) {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  return keys.map((key) => {
    if (key.length === 0 || (counts.get(key) ?? 0) < 2) {
      return key;
    }
    const index = seen.get(key) ?? 0;
    seen.set(key, index + 1);
    const letters = "abcdefghijklmnopqrstuvwxyz";
    const suffix = letters[index % letters.length];
    const round = Math.floor(index / letters.length);
    return round > 0 ? `${key}${suffix}${round}` : `${key}${suffix}`;
  });
}

function CopyButton({
  text,
  label,
  strings,
}: {
  text: string;
  label?: string;
  strings: Strings;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (await writeToClipboard(text)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error(strings.copyFailed);
    }
  }

  return (
    <button
      className="shrink-0 rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground text-xs hover:bg-muted"
      onClick={handleCopy}
      type="button"
    >
      {copied ? strings.copied : (label ?? strings.copy)}
    </button>
  );
}

function OptionPickers({
  format,
  setFormat,
  casing,
  setCasing,
  strings,
}: {
  strings: Strings;
  format: KeyFormat;
  setFormat: (f: KeyFormat) => void;
  casing: Casing;
  setCasing: (c: Casing) => void;
}) {
  return (
    <section className="space-y-4">
      <div className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.keyFormat}
        </p>
        <div className="flex flex-wrap gap-3">
          {KEY_FORMATS.map((f) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={f.value}
            >
              <input
                checked={format === f.value}
                className="accent-primary"
                name="key-format"
                onChange={() => setFormat(f.value)}
                type="radio"
                value={f.value}
              />
              <span>
                {strings[f.label]}{" "}
                <span className="font-mono text-muted-foreground text-xs">
                  ({f.example})
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.casing}</p>
        <div className="flex flex-wrap gap-3">
          {CASINGS.map((c) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={c.value}
            >
              <input
                checked={casing === c.value}
                className="accent-primary"
                name="key-casing"
                onChange={() => setCasing(c.value)}
                type="radio"
                value={c.value}
              />
              {strings[c.label]}
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const BIBTEX_KEY_GENERATOR_STRINGS = {
  generateASingleKey: "Generate a single key",
  authorLastNameS: "Author last name(s)",
  smithGarcia: "Smith, García",
  year: "Year",
  title: "Title",
  quantumEffectsIn: "Quantum effects in...",
  fillInAtLeastAn:
    "Fill in at least an author or a year to see the generated key.",
  batchModeOneReferencePer: "Batch mode — one reference per line",
  pasteFormattedReferencesApaHarvard:
    "Paste formatted references (APA, Harvard, or similar) — one per line. The author surname, year, and first title word are extracted on a best-effort basis, and colliding keys get a/b/c suffixes.",
  referencesToGenerateKeysFor: "References to generate keys for",
  generateKeys: "Generate Keys",
  copyAllKeys: "Copy all keys",
  whatMakesAGoodCitation: "What makes a good citation key?",
  predictable: "Predictable",
  aFixedSchemeLike: " — a fixed scheme like",
  authorYearFirstTitleWord: "author + year + first title word",
  meansYouCanOftenType: "means you can often type ",
  cite: "\\cite",
  keysFromMemoryWithoutOpening:
    "keys from memory without opening the .bib file.",
  asciiOnly: "ASCII-only",
  stripAccentsAndPunctuationMuller:
    " — strip accents and punctuation (Müller → muller, O'Brien → obrien). Non-ASCII keys break some BibTeX tools and are painful to type.",
  uniqueAndStable: "Unique and stable",
  neverRenameAKeyAfter:
    " — never rename a key after you start citing it. When an author has two papers in one year, add a/b/c suffixes rather than inventing a new scheme.",
  shortButMeaningful: "Short but meaningful",
  smith2024quantum: "smith2024quantum",
  beatsBoth: " beats both",
  smith2024: "smith2024",
  collidesEasilyAndAFull:
    " (collides easily) and a full title slug (tedious to type).",
  consistentlyCased: "Consistently cased",
  pickAllLowercaseOrCamelcase:
    " — pick all-lowercase or CamelCase once and stick to it; classic BibTeX ignores case when matching keys but Biber does not, so",
  smith20242: "Smith2024",
  and: " and",
  areDifferentKeys: " may be one key or two, depending on the tool.",
  formatAuthorYear: "author + year",
  formatAuthorYearFirstWord: "author + year + first title word",
  formatAuthorEtAlYear: "author + EtAl + year (multi-author)",
  casingLowercase: "all lowercase (smith2024quantum)",
  casingCamelcase: "CamelCase (Smith2024Quantum)",
  keyFormat: "Key format",
  casing: "Casing",
  copy: "Copy",
  copied: "Copied!",
  copyFailed: "Couldn't copy to clipboard. Please copy manually.",
  batchPlaceholder:
    "Smith, J. (2024) 'Quantum effects in biology', Nature, 600(1), pp. 1-10.\nSmith, J. (2024) 'Quantum computing at scale', Science, 380(2), pp. 20-31.",
  couldNotParseLine: "(could not parse this line)",
} as const;

export default function BibtexKeyGeneratorWidget() {
  const [format, setFormat] = useState<KeyFormat>("author-year-firstword");
  const [casing, setCasing] = useState<Casing>("lowercase");
  const [authors, setAuthors] = useState("");
  const [year, setYear] = useState("");
  const [title, setTitle] = useState("");
  const [batchInput, setBatchInput] = useState("");
  const [batchKeys, setBatchKeys] = useState<string[]>([]);

  const surnames = authors
    .split(RE_AUTHOR_SPLIT)
    .map((s) => s.trim())
    .filter(Boolean);
  const singleKey = buildKey({ surnames, year, title }, format, casing);

  function handleBatchGenerate() {
    const lines = batchInput
      .split(RE_NEWLINE)
      .map((l) => l.trim())
      .filter(Boolean);
    const keys = lines.map((line) =>
      buildKey(parseReference(line), format, casing)
    );
    setBatchKeys(disambiguate(keys));
  }

  const strings = useToolUi(BIBTEX_KEY_GENERATOR_STRINGS);

  return (
    <>
      <OptionPickers
        casing={casing}
        format={format}
        setCasing={setCasing}
        setFormat={setFormat}
        strings={strings}
      />
      {/* Single key from fields */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.generateASingleKey}
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="key-authors"
            >
              {strings.authorLastNameS}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="key-authors"
              onChange={(e) => setAuthors(e.target.value)}
              placeholder={strings.smithGarcia}
              type="text"
              value={authors}
            />
          </div>
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="key-year"
            >
              {strings.year}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="key-year"
              onChange={(e) => setYear(e.target.value)}
              placeholder="2024"
              type="text"
              value={year}
            />
          </div>
          <div className="space-y-2">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="key-title"
            >
              {strings.title}
            </label>
            <input
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="key-title"
              onChange={(e) => setTitle(e.target.value)}
              placeholder={strings.quantumEffectsIn}
              type="text"
              value={title}
            />
          </div>
        </div>
        {singleKey.length > 0 ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3">
            <code className="min-w-0 flex-1 truncate font-mono text-foreground text-sm">
              {singleKey}
            </code>
            <CopyButton strings={strings} text={singleKey} />
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            {strings.fillInAtLeastAn}
          </p>
        )}
      </section>
      {/* Batch mode */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.batchModeOneReferencePer}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.pasteFormattedReferencesApaHarvard}
        </p>
        <textarea
          aria-label={strings.referencesToGenerateKeysFor}
          className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          onChange={(e) => setBatchInput(e.target.value)}
          placeholder={strings.batchPlaceholder}
          value={batchInput}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
            disabled={batchInput.trim().length === 0}
            onClick={handleBatchGenerate}
            type="button"
          >
            {strings.generateKeys}
          </button>
          {batchKeys.length > 0 ? (
            <CopyButton
              label={strings.copyAllKeys}
              strings={strings}
              text={batchKeys.join("\n")}
            />
          ) : null}
        </div>
        {batchKeys.length > 0 ? (
          <div className="space-y-1.5">
            {batchKeys.map((key, i) => (
              <div
                className="flex items-center justify-between gap-3 rounded border border-border bg-card px-4 py-2"
                key={`batch-key-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: stable list order
                  i
                }`}
              >
                <code className="min-w-0 flex-1 truncate font-mono text-foreground text-sm">
                  {key.length > 0 ? key : "(could not parse this line)"}
                </code>
                {key.length > 0 ? (
                  <CopyButton strings={strings} text={key} />
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </section>
      {/* Explainer */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.whatMakesAGoodCitation}
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong>{strings.predictable}</strong>
            {strings.aFixedSchemeLike}{" "}
            <span className="font-mono">
              {strings.authorYearFirstTitleWord}
            </span>{" "}
            {strings.meansYouCanOftenType}
            <span className="font-mono">{strings.cite}</span>{" "}
            {strings.keysFromMemoryWithoutOpening}
          </li>
          <li>
            <strong>{strings.asciiOnly}</strong>
            {strings.stripAccentsAndPunctuationMuller}
          </li>
          <li>
            <strong>{strings.uniqueAndStable}</strong>
            {strings.neverRenameAKeyAfter}
          </li>
          <li>
            <strong>{strings.shortButMeaningful}</strong> —{" "}
            <span className="font-mono">{strings.smith2024quantum}</span>
            {strings.beatsBoth}{" "}
            <span className="font-mono">{strings.smith2024}</span>
            {strings.collidesEasilyAndAFull}
          </li>
          <li>
            <strong>{strings.consistentlyCased}</strong>
            {strings.pickAllLowercaseOrCamelcase}{" "}
            <span className="font-mono">{strings.smith20242}</span>
            {strings.and} <span className="font-mono">{strings.smith2024}</span>
            {strings.areDifferentKeys}
          </li>
        </ul>
      </section>
    </>
  );
}
